import React from 'react';
import { Tooltip } from 'antd';
import { useCosmetic } from './cosmetics-store';
import { nametagRarityOf, RARITY_LABEL_VI, FrameRarity } from './cosmetic-visuals';
import ImageNametagBanner, { BannerCfg } from './image-nametag-banner';
import mythicFrame from '../../images/cosmetics/mythic-nametag-frame.png';
import legendaryFrame from '../../images/cosmetics/legendary-nametag-frame.png';
import epicNametagFrame from '../../images/cosmetics/epic-nametag-frame.png';
import rareNametagFrame from '../../images/cosmetics/rare-nametag-frame.png';
import commonNametagFrame from '../../images/cosmetics/common-nametag-frame.png';

// Cấu hình khung ẢNH cho từng bậc (chỗ TO). Vị trí panel/slot đo từ chính ảnh khung.
//  - Thần Thoại/Huyền Thoại: khung LỬA (rồng/phượng), nền tối → chữ trắng + mưa tia lửa.
//  - Sử Thi/Hiếm/Cổ Điển: khung viền GEM, tâm TRONG SUỐT → chữ đổi màu theo sáng/tối, không tia lửa.
const BANNER_CFG: Record<string, BannerCfg> = {
  mythic: {
    frameImg: mythicFrame,
    aspect: '824 / 305',
    slot: { left: '48%', top: '47%', width: '46%' },
    magma: { left: '16%', right: '34%', bottom: '22%', height: '36%' },
    blaze: true,
  },
  legendary: {
    frameImg: legendaryFrame,
    aspect: '897 / 399',
    slot: { left: '44%', top: '59%', width: '46%' },
    // (đã bỏ hiệu ứng tia lửa cho nametag Huyền Thoại theo yêu cầu — chỉ còn khung phượng + tên)
  },
  epic: {
    frameImg: epicNametagFrame,
    aspect: '322 / 117',
    slot: { left: '50%', top: '50%', width: '76%' },
    nameClass: 'mnt-name-adaptive',
    dust: ['#da70d6', '#ffffff', '#ba55d3', '#dda0dd', '#9370db'],
  },
  rare: {
    frameImg: rareNametagFrame,
    aspect: '315 / 120',
    slot: { left: '50%', top: '50%', width: '76%' },
    nameClass: 'mnt-name-adaptive',
    dust: ['#00ffff', '#ffffff', '#1e90ff', '#00bfff', '#87cefa'],
  },
  common: {
    frameImg: commonNametagFrame,
    aspect: '314 / 115',
    slot: { left: '50%', top: '50%', width: '76%' },
    nameClass: 'mnt-name-adaptive',
    dust: ['#ffffff', '#b0c4de', '#e0ffff', '#87ceeb', '#dcdcdc'],
  },
};

interface NametagProps {
  userId?: string;
  children: React.ReactNode;
  className?: string;
  /** true → hiển thị khung ẢNH (banner) theo bậc. Mặc định false = CSS badge inline. */
  banner?: boolean;
  /** true → banner bản THU GỌN (feed/bình luận). */
  compact?: boolean;
  /** Ép hiển thị khung theo bậc này (dùng cho PREVIEW ở store, không cần user sở hữu). */
  previewRarity?: FrameRarity;
}

/**
 * Bọc tên hiển thị (text hoặc <Link>) trong "khung tên" theo bậc Nametag đang gắn.
 * Không gắn nametag → render children như cũ (không đổi gì).
 * Ép <a>/<span> con nhận màu của khung (!text-current) để thắng màu mặc định của Link.
 */
const Nametag: React.FC<NametagProps> = ({ userId, children, className, banner, compact, previewRarity }) => {
  const cos = useCosmetic(userId);
  const rarity = previewRarity ?? nametagRarityOf(cos?.nametag);

  if (!rarity) return <>{children}</>;

  // Rê chuột vào nametag → hiện "huy hiệu" (tên bậc).
  const badgeLabel = `Huy hiệu ${RARITY_LABEL_VI[rarity]}`;

  // Số thứ tự phát hành Thần Thoại (01..12) — chỉ khi đang gắn nametag Mythic thật (không phải preview).
  const mint = !previewRarity && rarity === 'mythic' ? cos?.nametag?.mintNumber : null;
  const mintText = mint ? `#${String(mint).padStart(2, '0')}` : '';
  const MintChip = mint ? (
    <span
      title={`Thần Thoại độc bản ${mintText}`}
      className="mt-0.5 inline-flex items-center rounded-full bg-gradient-to-r from-amber-400 to-fuchsia-500 px-1.5 text-[10px] font-bold leading-4 text-white shadow"
    >
      {mintText}
    </span>
  ) : null;

  // Khung ẢNH (banner) theo bậc — cả 5 bậc có khung riêng. compact = bản thu gọn (feed/bình luận).
  if (banner && BANNER_CFG[rarity]) {
    return (
      <Tooltip title={badgeLabel}>
        <span className="inline-flex flex-col items-center align-middle">
          <ImageNametagBanner cfg={BANNER_CFG[rarity]} className={`${compact ? 'mnt-compact' : ''} ${className || ''}`}>
            {children}
          </ImageNametagBanner>
          {MintChip /* số độc bản dưới nametag */}
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={badgeLabel}>
    <span className="inline-flex items-center gap-1">
    <span
      className={`nt-badge nt-${rarity} [&_a]:!text-current [&_span]:!text-current ${
        className || ''
      }`}
    >
      {rarity === 'legendary' && (
        <span aria-hidden className="nt-sweep nt-sweep-legend" />
      )}
      {rarity === 'mythic' && (
        <>
          <span aria-hidden className="nt-mythic-inner" />
          <span aria-hidden className="nt-sweep nt-sweep-mythic" />
        </>
      )}
      <span className="nt-text">{children}</span>
    </span>
    {MintChip}
    </span>
    </Tooltip>
  );
};

export default Nametag;
